import jsPDF from "jspdf";
import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import CustomDropdown from "../../../components/DropDown";
import logo from "../../../images/logo/1_logo.png";
import logo2 from "../../../images/logo/logo11.png";
import { apiPaths } from "../../../service/apiPaths";
import ApiService from "../../../service/ApiService";

// Company information
const COMPANY_INFO = {
  name: "Blisslers Technolabs LLP",
  address: "617 Blisslers Technolabs, Fortune Business Hub,",
  address2: "Science City, Road, Sola, Ahmedabad, Gujarat 380060",
  phone: "+91 9913055666",
  email: "hello@blisslers.com",
  logo: logo,
  logo2: logo2,
};

const FormGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "24px",
});

interface SalaryDetails {
  empName: string;
  empId: string;
  doj: string;
  designation: string;
  department: string;
  grossSalary: number;
  totalWorkingDays: number;
  leaves: number;
  variable: number;
  bonus: number;
  monthYear: string;
  extraDays: number;
  otherDeduction: number;
}

const calculateSalaryComponents = (details: SalaryDetails) => {
  const lopDays =
    details.leaves > 0 && details.leaves < 1
      ? details.leaves - 0.5
      : details.leaves > 0 && details.leaves >= 1
      ? details.leaves - 1
      : 0;
  const paidDays = details.totalWorkingDays - lopDays;

  const basic =
    (details.grossSalary / details.totalWorkingDays) * paidDays * 0.5;
  const hra = basic * 0.5;
  const otherAllowances =
    (details.grossSalary / details.totalWorkingDays) * paidDays -
    (basic + hra + details.variable);
  const extra =
    (details.grossSalary / details.totalWorkingDays) * details.extraDays;
  const totalEarnings =
    basic + hra + details.variable + otherAllowances + details.bonus + extra;
  const professionalTax = 200;
  const totalDeductionsCalc = professionalTax + (details.otherDeduction || 0);
  const netSalary = totalEarnings - totalDeductionsCalc;

  return {
    lopDays,
    paidDays,
    basic,
    hra,
    otherAllowances,
    totalEarnings,
    professionalTax,
    netSalary,
    extra,
    totalDeductionsCalc,
  };
};

// Number to words conversion function
const numberToWords = (num: number): string => {
  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const teens = [
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];

  const convertHundreds = (n: number): string => {
    let result = "";
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + " ";
      n %= 10;
    } else if (n >= 10) {
      result += teens[n - 10] + " ";
      n = 0;
    }
    if (n > 0) {
      result += ones[n] + " ";
    }
    return result;
  };

  if (num === 0) return "Zero Rupees Only";

  const crores = Math.floor(num / 10000000);
  const lakhs = Math.floor((num % 10000000) / 100000);
  const thousands = Math.floor((num % 100000) / 1000);
  const hundreds = num % 1000;

  let result = "";
  if (crores > 0) result += convertHundreds(crores) + "Crore ";
  if (lakhs > 0) result += convertHundreds(lakhs) + "Lakh ";
  if (thousands > 0) result += convertHundreds(thousands) + "Thousand ";
  if (hundreds > 0) result += convertHundreds(hundreds);

  return result.trim() + " Rupees Only";
};

const SalarySlip: React.FC = () => {
  const [user, setUser] = useState<any[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<SalaryDetails>({
    empName: "",
    empId: "",
    doj: "",
    designation: "",
    department: "",
    grossSalary: 0,
    totalWorkingDays: 0,
    leaves: 0,
    variable: 0,
    bonus: 0,
    monthYear: new Date().toISOString().slice(0, 7),
    extraDays: 0,
    otherDeduction: 0,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getEmployees}`,
      });
      setUser([{ id: null, name: "select User" }, ...result]);
      setUserId(null);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchSelectUser(userId);
    }
  }, [userId]);

  const fetchSelectUser = async (id: any) => {
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getAllEmployees}/${id}`,
      });
      setFormData((prev) => ({
        ...prev,
        empName: result.name,
        empId: result.empId,
        doj: new Date(result.doj).toISOString().split("T")[0],
        designation: result.designation,
        department: result.department,
      }));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name.includes("Salary") ||
        name.includes("Days") ||
        name === "variable" ||
        name === "bonus" ||
        name === "otherDeduction"
          ? parseFloat(value) || 0
          : value,
    }));
  };

  const downloadPDF = () => {
    const pdf = new jsPDF();
    const calculations = calculateSalaryComponents(formData);

    // Page setup
    const margin = 15;
    let yPos = margin;
    const pageWidth = pdf.internal.pageSize.width;
    const contentWidth = pageWidth - 2 * margin;

    // Header section with company info
    // pdf.setFillColor(248, 249, 250);
    // pdf.roundedRect(0, 0, pageWidth, 35, 6, 6, "F");

    // Company logo (if available)
    const logo2Width = 18;
    const logo2Height = 20;

    // Add logo2
    pdf.addImage(logo2, "PNG", margin, yPos - 5, logo2Width, logo2Height);

    // Add logo
    //   pdf.addImage(
    //     logo,
    //     "PNG",
    //     margin + logo2Width + 10,
    //     yPos - 5,
    //     logoWidth,
    //     logoHeight
    //   );

    // Company name at the top
    pdf.setFontSize(18);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont("helvetica", "bold");
    pdf.text(`${COMPANY_INFO.name}`, margin + logo2Width + 2, yPos + 4);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(105, 103, 99);
    pdf.text(`${COMPANY_INFO.address}`, margin + logo2Width + 2, yPos + 8);
    pdf.text(`${COMPANY_INFO.address2}`, margin + logo2Width + 2, yPos + 12);
    pdf.setTextColor(0, 0, 0);

    // Payslip title aligned right
    yPos += 4;
    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(105, 103, 99);
    pdf.text("Payslip For the Month", pageWidth - margin - 50, (yPos -= 2));
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(15);
    yPos += 2.5;
    const date = new Date(formData.monthYear);
    const formattedDate =
      date.toLocaleString("default", { month: "long" }) +
      "-" +
      date.getFullYear();
    pdf.text(formattedDate, pageWidth - margin - 50, yPos + 5);
    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.line(margin, yPos + 15, pageWidth - margin, yPos + 15);
    pdf.setDrawColor(5, 5, 5);
    pdf.setLineWidth(0.5);
    yPos = 45;

    // Employee Summary section - white background, light border, rounded
    pdf.setDrawColor(200, 200, 200);
    pdf.setFillColor(255, 255, 255);
    // pdf.roundedRect(margin, yPos, contentWidth, 50, 6, 6, "FD");

    // Left side - Employee details
    pdf.setTextColor(52, 58, 64);
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "bold");
    pdf.text("EMPLOYEE SUMMARY", margin + 5, yPos + 8);

    let leftYPos = yPos + 18;
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(52, 58, 64);

    const employeeDetails = [
      ["Employee Name", formData.empName],
      ["Employee ID", formData.empId],
      ["Designation", formData.designation],
      ["Date of joining", formData.doj],
    ];

    employeeDetails.forEach(([label, value]) => {
      pdf.text(`${label}:`, margin + 5, leftYPos);
      pdf.text(String(value), margin + 45, leftYPos);
      leftYPos += 7;
    });

    // Right side - Net pay highlight (styled like screenshot)
    const netPayBoxX = pageWidth - margin - 60;
    const netPayBoxY = yPos + 3;
    const netPayBoxW = 60;
    const netPayBoxH = 41;
    // Box background
    pdf.setDrawColor(220, 220, 220);
    pdf.setFillColor(235, 245, 255); // very light green
    pdf.roundedRect(netPayBoxX, netPayBoxY, netPayBoxW, netPayBoxH, 4, 4, "FD");
    // Left green accent line
    pdf.setLineWidth(0.2); // reset
    // Amount (large, bold, black)
    pdf.setFontSize(18);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    pdf.text(
      `Rs.${calculations.netSalary.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })}`,
      netPayBoxX + 10,
      netPayBoxY + 9
    );
    // Label (smaller, gray)
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(108, 117, 125);
    pdf.text("Total Net Pay", netPayBoxX + 10, netPayBoxY + 15);
    // Divider line
    pdf.setDrawColor(220, 220, 220);
    pdf.line(
      netPayBoxX + 8,
      netPayBoxY + 18,
      netPayBoxX + netPayBoxW - 8,
      netPayBoxY + 18
    );
    // Paid Days and LOP Days (labels left, values right)
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
    pdf.text("Paid Days", netPayBoxX + 10, netPayBoxY + 23);
    pdf.text(":", netPayBoxX + 42, netPayBoxY + 23);
    pdf.text(
      String(calculations.paidDays),
      netPayBoxX + netPayBoxW - 10,
      netPayBoxY + 23,
      { align: "right" }
    );
    pdf.text("Leaves", netPayBoxX + 10, netPayBoxY + 28);
    pdf.text(":", netPayBoxX + 42, netPayBoxY + 28);
    pdf.text(
      String(formData.leaves),
      netPayBoxX + netPayBoxW - 10,
      netPayBoxY + 28,
      { align: "right" }
    );
    pdf.text("LOP Days", netPayBoxX + 10, netPayBoxY + 33);
    pdf.text(":", netPayBoxX + 42, netPayBoxY + 33);
    pdf.text(
      String(calculations.lopDays),
      netPayBoxX + netPayBoxW - 10,
      netPayBoxY + 33,
      { align: "right" }
    );
    pdf.text("Total working Days", netPayBoxX + 10, netPayBoxY + 38);
    pdf.text(":", netPayBoxX + 42, netPayBoxY + 38);
    pdf.text(
      String(formData.totalWorkingDays),
      netPayBoxX + netPayBoxW - 10,
      netPayBoxY + 38,
      { align: "right" }
    );

    yPos += 55;

    // Earnings and Deductions table
    const tableStartY = yPos;
    const tableHeight = 8;
    const colWidth = contentWidth / 4;

    // Table headers
    pdf.setFillColor(242, 246, 250);
    pdf.roundedRect(margin, yPos, contentWidth, tableHeight, 4, 4, "F");
    pdf.setTextColor(52, 58, 64);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.text("EARNINGS", margin + 5, yPos + 5);
    pdf.text("AMOUNT", margin + colWidth + 22, yPos + 5);
    pdf.text("DEDUCTIONS", margin + colWidth * 2 + 10, yPos + 5);
    pdf.text("AMOUNT", margin + colWidth * 3 + colWidth - 18, yPos + 5);

    yPos += tableHeight;

    // Table rows
    const earnings = [
      ["Basic", calculations.basic],
      ["House Rent Allowance", calculations.hra],
      ["Variable Pay", formData.variable],
      ["Bonus", formData.bonus + calculations.extra],
      ["Allowances", calculations.otherAllowances],
    ];

    const deductions = [
      ["Income Tax", 0],
      ["Provident Fund", 0],
      ["Professional Tax", calculations.professionalTax],
      ["Other Deduction", formData.otherDeduction || 0],
      ["", 0],
    ];

    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(108, 117, 125);
    pdf.setFontSize(9);

    let totalEarnings = 0;
    let totalDeductions = calculations.professionalTax + (formData.otherDeduction || 0);

    earnings.forEach((earning, index) => {
      const rowY = yPos + index * 6;
      // Earnings
      pdf.text(String(earning[0]), margin + 5, rowY + 4);
      pdf.text(
        `${earning[1].toLocaleString("en-IN", {
          minimumFractionDigits: 2,
        })}`,
        margin + colWidth + 35,
        rowY + 4,
        { align: "right" }
      );
      totalEarnings += Number(earning[1]);
      // Deductions
      if (deductions[index] && deductions[index][0]) {
        pdf.text(
          String(deductions[index][0]),
          margin + colWidth * 2 + 10,
          rowY + 4
        );
        pdf.text(
          `${deductions[index][1].toLocaleString("en-IN", {
            minimumFractionDigits: 2,
          })}`,
          margin + colWidth * 3 + colWidth - 5,
          rowY + 4,
          { align: "right" }
        );
      }
    });

    yPos += earnings.length * 6 + 5;

    // Totals row
    pdf.setFillColor(242, 246, 250);
    pdf.roundedRect(margin, yPos, contentWidth, tableHeight, 4, 4, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(52, 58, 64);
    pdf.text("Gross Earnings", margin + 5, yPos + 5);
    pdf.text(
      `Rs.${totalEarnings.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })}`,
      margin + colWidth + 35,
      yPos + 5,
      { align: "right" }
    );
    pdf.text("Total Deductions", margin + colWidth * 2 + 10, yPos + 5);
    pdf.text(
      `Rs.${totalDeductions.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })}`,
      margin + colWidth * 3 + colWidth - 5,
      yPos + 5,
      { align: "right" }
    );

    yPos += tableHeight + 10;

    // Net payable section (green box, bold, right-aligned)
    pdf.setFillColor(227, 240, 252); // Light green
    pdf.roundedRect(margin, yPos, contentWidth, 20, 6, 6, "F");
    pdf.setTextColor(52, 58, 64);
    pdf.setFontSize(11);
    pdf.setFont("helvetica", "bold");
    pdf.text("TOTAL NET PAYABLE", margin + 5, yPos + 8);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.text("Gross Earnings - Total Deductions", margin + 5, yPos + 15);
    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.text(
      `Rs.${calculations.netSalary.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
      })}`,
      pageWidth - margin - 5,
      yPos + 12,
      { align: "right" }
    );

    yPos += 30;

    // Amount in words (small font)
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(52, 58, 64);
    pdf.text(
      `-- This Salary slip is auto generated by computer which does not require signature. --`,
      margin + 25,
      yPos
    );

    yPos += 20;

    // Save the PDF
    pdf.save(`salary-slip-${formData.empName}-${formData.monthYear}.pdf`);
  };

  return (
    <Container maxWidth="md" className="h-[80vh] overflow-y-auto">
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h4" gutterBottom>
          Salary Slip Generator
        </Typography>
        <CustomDropdown
          label="Employee Select:"
          options={user}
          defaultOptionId={userId || ""}
          setDefaultOptionId={setUserId}
          displayProperty="name"
          value={userId || ""}
          style="flex items-center border h-12 rounded-lg shadow-none relative text-sm border-border text-fontColor w-full mb-4"
        />
        <FormGrid>
          <TextField
            fullWidth
            label="Employee Name"
            name="empName"
            value={formData.empName}
            onChange={handleInputChange}
          />
          <TextField
            fullWidth
            label="Employee ID"
            name="empId"
            value={formData.empId}
            onChange={handleInputChange}
          />
          <TextField
            fullWidth
            label="Date of Joining"
            name="doj"
            type="date"
            value={formData.doj}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Designation"
            name="designation"
            value={formData.designation}
            onChange={handleInputChange}
          />
          <TextField
            fullWidth
            label="Department"
            name="department"
            value={formData.department}
            onChange={handleInputChange}
          />
          <TextField
            fullWidth
            label="Month/Year"
            name="monthYear"
            type="month"
            value={formData.monthYear}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Gross Salary"
            name="grossSalary"
            type="number"
            value={formData.grossSalary}
            onChange={handleInputChange}
            InputProps={{
              inputProps: {
                min: 0,
              },
            }}
          />
          <TextField
            fullWidth
            label="Total Working Days"
            name="totalWorkingDays"
            type="number"
            value={formData.totalWorkingDays}
            onChange={handleInputChange}
            InputProps={{
              inputProps: {
                min: 0,
              },
            }}
          />
          <TextField
            fullWidth
            label="Leaves"
            name="leaves"
            type="number"
            value={formData.leaves}
            onChange={handleInputChange}
            InputProps={{
              inputProps: {
                min: 0,
              },
            }}
          />
          <TextField
            fullWidth
            label="Extra Days"
            name="extraDays"
            type="number"
            value={formData.extraDays}
            onChange={handleInputChange}
            InputProps={{
              inputProps: {
                min: 0,
              },
            }}
          />
          <TextField
            fullWidth
            label="Variable Pay"
            name="variable"
            type="number"
            value={formData.variable}
            onChange={handleInputChange}
            InputProps={{
              inputProps: {
                min: 0,
              },
            }}
          />
          <TextField
            fullWidth
            label="Bonus"
            name="bonus"
            type="number"
            value={formData.bonus}
            onChange={handleInputChange}
            InputProps={{
              inputProps: {
                min: 0,
              },
            }}
          />
          <TextField
            fullWidth
            label="Other Deduction"
            name="otherDeduction"
            type="number"
            value={formData.otherDeduction}
            onChange={handleInputChange}
            InputProps={{
              inputProps: {
                min: 0,
              },
            }}
          />
        </FormGrid>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={downloadPDF}
            disabled={!formData.empName || !formData.empId}
          >
            Generate Salary Slip
          </Button>
        </Box>

        {/* Preview Section */}
        {formData.empName && formData.grossSalary > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Preview Calculations
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Component</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const calculations = calculateSalaryComponents(formData);
                    return (
                      <>
                        <TableRow>
                          <TableCell>Basic</TableCell>
                          <TableCell align="right">
                            {calculations.basic.toFixed(2)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>HRA</TableCell>
                          <TableCell align="right">
                            {calculations.hra.toFixed(2)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Variable</TableCell>
                          <TableCell align="right">
                            {formData.variable}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                      <TableCell>Extra Days Pay</TableCell>
                          <TableCell align="right">
                            {calculations.extra.toFixed(2)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Bonus</TableCell>
                          <TableCell align="right">{formData.bonus}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Allowances</TableCell>
                          <TableCell align="right">
                            {calculations.otherAllowances.toFixed(2)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Professional Tax</TableCell>
                          <TableCell align="right">
                            {calculations.professionalTax}
                          </TableCell>
                        </TableRow>
                    <TableRow>
                      <TableCell>Other Deduction</TableCell>
                      <TableCell align="right">
                        {formData.otherDeduction}
                      </TableCell>
                    </TableRow>
                        <TableRow>
                          <TableCell>
                            <strong>Net Salary</strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>{calculations.netSalary.toFixed(2)}</strong>
                          </TableCell>
                        </TableRow>
                      </>
                    );
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default SalarySlip;
